/** 유입 탭 — how an app is found (`/apps/:id/sources`).
 *
 *  Apple's funnel split by source type, the countries search brings, and what
 *  the listing said and when, so a change to a name can be read against what
 *  came after it. The window ends on the newest day stored; Apple's files run
 *  two or three days behind and hide small counts, so the parts can fall short
 *  of the app's funnel total. */

import type { App, FunnelSources, ListingRow } from '@/api/client'
import { useFunnelSources, useListingHistory } from '@/lib/queries'
import { formatDay } from '@/lib/time'
import { Bars, CountryTag, DataTable, EmptyState, type Column } from '@/ui'

const SOURCE_LABEL: Record<string, string> = {
  'App Store search': '검색',
  'App Store browse': '둘러보기',
  'App referrer': '앱 추천',
  'Web referrer': '웹 추천',
  'Institutional purchase': '기관 구매',
  Unavailable: '알 수 없음',
  '': '알 수 없음',
}

const n = (v: number) => v.toLocaleString('ko-KR')

type SourceRow = FunnelSources['sources'][number]
type CountryRow = FunnelSources['countries'][number]

const SOURCE_COLUMNS: Column<SourceRow>[] = [
  { key: 'source', header: '경로', card: 'title', cell: (s) => <span className="font-medium">{SOURCE_LABEL[s.source_type] ?? s.source_type}</span> },
  { key: 'impressions', header: '노출', align: 'right', card: 'line', className: 'tabular-nums', cell: (s) => n(s.impressions) },
  { key: 'views', header: '페이지 조회', align: 'right', card: 'line', className: 'tabular-nums', cell: (s) => n(s.page_views) },
  { key: 'downloads', header: '첫 다운로드', align: 'right', card: 'line', className: 'tabular-nums', cell: (s) => n(s.first_downloads) },
  {
    key: 'cvr',
    header: '전환율',
    align: 'right',
    card: 'line',
    className: 'tabular-nums',
    cell: (s) => (s.impressions > 0 ? `${((s.first_downloads / s.impressions) * 100).toFixed(1)}%` : '—'),
  },
]

const COUNTRY_COLUMNS: Column<CountryRow>[] = [
  { key: 'country', header: '국가', card: 'title', cell: (c) => <CountryTag code={c.country} /> },
  { key: 'impressions', header: '검색 노출', align: 'right', card: 'line', className: 'tabular-nums', cell: (c) => n(c.search_impressions) },
  { key: 'search', header: '검색 다운로드', align: 'right', card: 'line', className: 'tabular-nums', cell: (c) => n(c.search_downloads) },
  { key: 'all', header: '전체 다운로드', align: 'right', card: 'line', className: 'tabular-nums', cell: (c) => n(c.downloads) },
]

const LISTING_COLUMNS: Column<ListingRow>[] = [
  { key: 'date', header: '날짜', width: '104px', card: 'meta', className: 'whitespace-nowrap tabular-nums text-muted-foreground', cell: (l) => formatDay(l.captured_date) },
  { key: 'locale', header: '로케일', width: '88px', card: 'meta', className: 'text-muted-foreground', cell: (l) => l.locale },
  {
    key: 'name',
    header: '이름 · 부제',
    card: 'title',
    cell: (l) => (
      <span className="min-w-0">
        <span className="block truncate font-medium">{l.name}</span>
        {l.subtitle && <span className="block truncate text-meta text-muted-foreground">{l.subtitle}</span>}
      </span>
    ),
  },
]

export function SourcesTab({ app }: { app: App }) {
  const sources = useFunnelSources(app.id)
  const history = useListingHistory(app.id)
  const d = sources.data
  const days = d?.days ?? []
  return (
    <div className="space-y-6">
      <section aria-label="유입 경로" className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <h2 className="text-section font-semibold">유입 경로</h2>
          {d?.from && (
            <span className="text-meta tabular-nums text-muted-foreground">
              {formatDay(d.from)} ~ {formatDay(d.to)}
            </span>
          )}
        </div>
        <DataTable
          label="유입 경로"
          rows={d?.sources}
          rowKey={(s) => s.source_type}
          columns={SOURCE_COLUMNS}
          loading={sources.isLoading}
          error={sources.error}
          onRetry={() => sources.refetch()}
          empty={<EmptyState title="유입 기록이 아직 없다" description="App Store Analytics를 받으면 Apple이 남긴 최근 5주가 채워진다." />}
        />
      </section>

      {days.length > 1 && (
        <section aria-label="검색 다운로드 추이" className="space-y-2">
          <h2 className="text-section font-semibold">검색 다운로드</h2>
          <Bars
            label="검색 다운로드 추이"
            labels={days.map((x) => x.date)}
            outer={days.map((x) => x.search_downloads)}
            height={64}
            title={(i) => `${formatDay(days[i].date)} 검색 ${n(days[i].search_downloads)} · 전체 ${n(days[i].downloads)}`}
            tick={(l) => formatDay(l, undefined, false)}
          />
        </section>
      )}

      {(d?.countries.length ?? 0) > 0 && (
        <section aria-label="국가별 검색" className="space-y-2">
          <h2 className="text-section font-semibold">국가별 검색</h2>
          <DataTable label="국가별 검색" rows={d?.countries} rowKey={(c) => c.country} columns={COUNTRY_COLUMNS} empty={<EmptyState title="기록이 없다" />} />
        </section>
      )}

      <section aria-label="스토어 문구 이력" className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
          <h2 className="text-section font-semibold">스토어 문구 이력</h2>
          <span className="text-meta text-muted-foreground">처음 읽은 날과 바뀐 날만 남는다</span>
        </div>
        <DataTable
          label="스토어 문구 이력"
          rows={history.data?.items}
          rowKey={(l) => `${l.locale}-${l.captured_date}`}
          columns={LISTING_COLUMNS}
          loading={history.isLoading}
          error={history.error}
          onRetry={() => history.refetch()}
          empty={<EmptyState title="문구 이력이 아직 없다" description="ASC 동기화가 돌면 로케일마다 쌓인다." />}
        />
      </section>
    </div>
  )
}
